import { css } from "@emotion/css";
import type { BadgeVariant } from "@octopusdeploy/design-system-components";
import { Badge, Callout, SimpleDataTable, Tag, Tooltip } from "@octopusdeploy/design-system-components";
import { UnavailableIcon } from "@octopusdeploy/design-system-icons";
import { Avatar, TaskStatus } from "@octopusdeploy/design-system-octopus-components";
import { borderRadius, fontSize, fontWeight, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { DeploymentTaskDetails, ProjectDetails, ProjectEphemeralEnvironmentDetail, ProjectResource, RunbookRunTaskDetails, EphemeralEnvironmentsTaskStatus } from "@octopusdeploy/octopus-server-client";
import { HasRunbooksInGit, TaskState } from "@octopusdeploy/octopus-server-client";
import type { LinkHref } from "@octopusdeploy/portal-routes";
import { links } from "@octopusdeploy/portal-routes";
import cn from "classnames";
import pluralize from "pluralize";
import { useState } from "react";
import taskStatusIconStyles from "~/areas/projects/components/TaskStatusIcon/style.module.less";
import { client } from "~/clientInstance";
import OkDialogLayout from "~/components/DialogLayout/OkDialogLayout";
import type { MenuItem } from "~/components/OverflowMenu/OverflowMenu";
import { OverflowMenu, OverflowMenuItems } from "~/components/OverflowMenu/OverflowMenu";
import { useOctopusFeatureToggle } from "~/hooks/useOctopusFeatureToggle";
import DateFormatter from "~/utils/DateFormatter";
import { TaskStatusDetails } from "../Runbooks/NewRunbookRunsListPage/TaskStatusDetails";
import { DeprovisionEnvironmentDialog } from "./DeprovisionEnvironmentDialog";
import { EphemeralEnvironmentStatusIcon } from "./EphemeralEnvironmentStateIcon";
type EphemeralEnvironmentsTableProps = {
    project: ProjectResource;
    environments: ProjectEphemeralEnvironmentDetail[];
    retryProvisioning: (environment: ProjectEphemeralEnvironmentDetail) => Promise<void>;
    retryDeprovisioning: (environment: ProjectEphemeralEnvironmentDetail) => Promise<void>;
    markProvisioningSuccessful: (environment: ProjectEphemeralEnvironmentDetail) => Promise<void>;
    markDeprovisioningSuccessful: (environment: ProjectEphemeralEnvironmentDetail) => Promise<void>;
    reloadEnvironmentsForProject: () => void;
};
export function EphemeralEnvironmentsTable({ project, environments, retryProvisioning, retryDeprovisioning, markProvisioningSuccessful, markDeprovisioningSuccessful, reloadEnvironmentsForProject }: EphemeralEnvironmentsTableProps) {
    return (<SimpleDataTable<ProjectEphemeralEnvironmentDetail> columns={[
            { title: "Name", columnSize: "medium", render: (environment) => <EphemeralEnvironmentName environment={environment}/> },
            { title: "Provisioning Status", columnSize: "small", render: (environment) => <EphemeralEnvironmentProjectStatusBadge environment={environment}/> },
            { title: "Latest Provisioning Task", columnSize: "medium", render: (environment) => <RunbookRunTask project={project} task={environment.RunbookRunTaskDetails} environment={environment}/> },
            { title: "Latest Deployment", columnSize: "medium", render: (environment) => <DeploymentTask project={project} task={environment.DeploymentTaskDetails}/> },
            { title: "Projects", columnSize: "medium", render: (environment) => <Projects projectDetails={environment.ProjectDetails}/> },
            {
                title: "",
                columnSize: "x-small",
                render: (environment) => (<ProjectEphemeralEnvironmentOverflowMenu environment={environment} project={project} retryProvisioning={retryProvisioning} retryDeprovisioning={retryDeprovisioning} markProvisioningSuccessful={markProvisioningSuccessful} markDeprovisioningSuccessful={markDeprovisioningSuccessful} reloadEnvironmentsForProject={reloadEnvironmentsForProject}/>),
            },
        ]} data={environments} getRowKey={(environment) => environment.Id}/>);
}
interface EphemeralEnvironmentColumnProps {
    environment: ProjectEphemeralEnvironmentDetail;
}
function EphemeralEnvironmentName({ environment }: EphemeralEnvironmentColumnProps) {
    const styles = {
        name: css({
            font: text.body.bold.medium,
        }),
    };
    return <div className={styles.name}>{environment.Name}</div>;
}
function EphemeralEnvironmentProjectStatusBadge({ environment }: EphemeralEnvironmentColumnProps) {
    const styles = {
        badgeWrapper: css({
            whiteSpace: "nowrap",
        }),
    };
    interface StatusConfig {
        variant: BadgeVariant;
        label: string;
    }
    const statusMap: Record<string, StatusConfig> = {
        NotProvisioned: { variant: "neutral", label: "Not Provisioned" },
        Provisioning: { variant: "info", label: "Provisioning" },
        ProvisioningFailed: { variant: "danger", label: "Provisioning failed" },
        Provisioned: { variant: "success", label: "Provisioned" },
        Deprovisioning: { variant: "info", label: "Deprovisioning" },
        DeprovisioningFailed: { variant: "danger", label: "Deprovisioning failed" },
        Deprovisioned: { variant: "neutral", label: "Deprovisioned" },
    };
    const icon = <EphemeralEnvironmentStatusIcon status={environment.Status} iconSize={20}/>;
    const badgeSize = "medium";
    const statusConfig = statusMap[environment.Status];
    if (!statusConfig) {
        return <></>;
    }
    return (<div className={styles.badgeWrapper}>
            <Badge size={badgeSize} variant={statusConfig.variant} label={statusConfig.label} icon={icon}/>
        </div>);
}
interface EphemeralEnvironmentTaskProps {
    project: ProjectResource;
}
interface DeploymentTaskProps extends EphemeralEnvironmentTaskProps {
    task: DeploymentTaskDetails | undefined;
}
interface RunbookRunTaskProps extends EphemeralEnvironmentTaskProps {
    task: RunbookRunTaskDetails | undefined;
    environment: ProjectEphemeralEnvironmentDetail;
}
function DeploymentTask({ project, task }: DeploymentTaskProps) {
    if (!task) {
        return <ProjectEphemeralEnvironmentNoTaskStatus title={"No tasks"}/>;
    }
    const link = links.deploymentPage.generateUrl({
        spaceId: project.SpaceId,
        projectSlug: project.Slug,
        releaseVersion: task.ReleaseVersion,
        deploymentId: task.DeploymentId,
    });
    return <ProjectEphemeralEnvironmentTaskStatusDetails task={task.TaskStatus} title={task.ReleaseVersion} link={link}/>;
}
function RunbookRunTask({ project, task, environment }: RunbookRunTaskProps) {
    if (!task) {
        switch (environment.Status) {
            case "Deprovisioned":
                return <ProjectEphemeralEnvironmentNoTaskStatus title={"No deprovisioning runbook configured"}/>;
            case "Provisioned":
                return <ProjectEphemeralEnvironmentNoTaskStatus title={"No provisioning runbook configured"}/>;
            case "ProvisioningFailed":
                return (<ProjectEphemeralEnvironmentErrorTaskStatus error={environment.ProvisioningFailureDetails?.Message || "An error occurred during provisioning"} subtitle={DateFormatter.dateToShortFormat(environment.ProvisioningFailureDetails?.Occurred) ?? ""}/>);
            case "DeprovisioningFailed":
                return (<ProjectEphemeralEnvironmentErrorTaskStatus error={environment.DeprovisioningFailureDetails?.Message || "An error occurred during deprovisioning"} subtitle={DateFormatter.dateToShortFormat(environment.DeprovisioningFailureDetails?.Occurred) ?? ""}/>);
            default:
                return <ProjectEphemeralEnvironmentNoTaskStatus title={"No tasks"}/>;
        }
    }
    const title = task.RunName;
    const link = links.runbookRunPage.generateUrl({
        spaceId: project.SpaceId,
        projectSlug: project.Slug,
        runbookId: task.RunbookId,
        runbookSnapshotId: task.RunbookSnapshotId,
        runbookRunId: task.RunbookRunId,
    });
    return <ProjectEphemeralEnvironmentTaskStatusDetails task={task.TaskStatus} title={title} link={link}/>;
}
function ProjectEphemeralEnvironmentTaskStatusDetails({ task, title, link }: {
    task: EphemeralEnvironmentsTaskStatus;
    title: string;
    link: LinkHref;
}) {
    const subtitle = `${DateFormatter.dateToShortFormat(task.CompletedTime ?? task.StartTime ?? task.QueueTime ?? task.Created)}`;
    const styles = {
        taskStatus: css({
            display: "flex",
            alignItems: "center",
            overflow: "hidden", // Fallback/safety-net in case dates or anything else overflows here.
            minHeight: "3.25rem",
            borderRadius: borderRadius.small,
            marginLeft: `-${space[8]}`,
            "&:hover": {
                cursor: "pointer",
                outline: `1px solid ${themeTokens.color.border.selected}`,
                backgroundColor: themeTokens.color.background.primary.hover,
            },
        }),
    };
    return (<div className={styles.taskStatus}>
            <TaskStatusDetails task={task} title={title} subtitle={subtitle} link={link}/>
        </div>);
}
function ProjectEphemeralEnvironmentNoTaskStatus({ title }: {
    title: string;
}) {
    const noDataStyles = css({
        display: "flex",
        gap: space[6],
        font: text.body.bold.medium,
        color: themeTokens.color.text.disabled,
        alignItems: "center",
        padding: space[4],
    });
    return (<div className={noDataStyles}>
            <UnavailableIcon size={24}/>
            {title}
        </div>);
}
function ProjectEphemeralEnvironmentErrorTaskStatus({ error, subtitle }: {
    error: string;
    subtitle?: string;
}) {
    const isNewStatusIconsFeatureEnabled = useOctopusFeatureToggle("new-status-icons", false);
    const componentStyles = {
        errorData: css({
            display: "flex",
            gap: space[8],
            font: text.body.bold.medium,
            alignItems: "center",
        }),
        statusIcon: css({
            width: "2rem",
            height: "2rem",
            fontSize: "1.25rem",
        }),
        subtitle: css({
            color: themeTokens.color.text.tertiary,
            fontWeight: fontWeight[400],
            fontSize: fontSize.xSmall,
        }),
        details: css({
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            alignItems: "flex-start",
            width: "100%",
        }),
        errorText: css({
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "16rem",
        }),
    };
    const statusIcon = isNewStatusIconsFeatureEnabled ? (<TaskStatus status="failed" size={32}/>) : (<span role="img" className={cn(taskStatusIconStyles.status, taskStatusIconStyles[TaskState.Failed], componentStyles.statusIcon)} aria-label="Failed">
            <i className="fa-solid fa-xmark"/>
        </span>);
    return (<Tooltip content={`An error occurred while attempting to create this task: ${error}`}>
            <div className={componentStyles.errorData}>
                {statusIcon}
                <div className={componentStyles.details}>
                    <span className={componentStyles.errorText}>{error}</span>
                    {subtitle && <span className={componentStyles.subtitle}>{subtitle}</span>}
                </div>
            </div>
        </Tooltip>);
}
type ProjectEphemeralEnvironmentOverflowMenuProps = {
    environment: ProjectEphemeralEnvironmentDetail;
    project: ProjectResource;
    retryProvisioning: (environment: ProjectEphemeralEnvironmentDetail) => Promise<void>;
    retryDeprovisioning: (environment: ProjectEphemeralEnvironmentDetail) => Promise<void>;
    markProvisioningSuccessful: (environment: ProjectEphemeralEnvironmentDetail) => Promise<void>;
    markDeprovisioningSuccessful: (environment: ProjectEphemeralEnvironmentDetail) => Promise<void>;
    reloadEnvironmentsForProject: () => void;
};
function ProjectEphemeralEnvironmentOverflowMenu({ environment, project, retryProvisioning, retryDeprovisioning, markProvisioningSuccessful, markDeprovisioningSuccessful, reloadEnvironmentsForProject }: ProjectEphemeralEnvironmentOverflowMenuProps) {
    const overFlowActions: Array<MenuItem | MenuItem[]> = [];
    switch (environment.Status) {
        case "Provisioned":
        case "Deprovisioned":
        case "NotProvisioned":
            overFlowActions.push(OverflowMenuItems.dialogItem("Deprovision environment", <DeprovisionEnvironmentDialog environment={environment} project={project} reloadEnvironmentsForProject={reloadEnvironmentsForProject}/>));
            break;
        case "ProvisioningFailed":
            overFlowActions.push(OverflowMenuItems.dialogItem("Retry provisioning", <RetryProvisioningDialog environment={environment} project={project} retryProvisioning={retryProvisioning}/>), OverflowMenuItems.dialogItem("Mark as successful", <MarkProvisioningSuccessfulDialog environment={environment} markProvisioningSuccessful={markProvisioningSuccessful}/>));
            break;
        case "DeprovisioningFailed":
            overFlowActions.push(OverflowMenuItems.dialogItem("Retry deprovisioning", <RetryDeprovisioningDialog environment={environment} project={project} retryDeprovisioning={retryDeprovisioning}/>), OverflowMenuItems.dialogItem("Mark as successful", <MarkDeprovisioningSuccessfulDialog environment={environment} markDeprovisioningSuccessful={markDeprovisioningSuccessful}/>));
            break;
    }
    return <OverflowMenu menuItems={overFlowActions}/>;
}
type RetryProvisioningDialogProps = {
    environment: ProjectEphemeralEnvironmentDetail;
    project: ProjectResource;
    retryProvisioning: (environment: ProjectEphemeralEnvironmentDetail) => Promise<void>;
};
function RetryProvisioningDialog({ environment, project, retryProvisioning }: RetryProvisioningDialogProps) {
    const message = HasRunbooksInGit(project.PersistenceSettings)
        ? "This will retry provisioning from the latest commit on the Git ref used in the previous attempt"
        : "This will retry provisioning from the latest published snapshot of the runbook used in the previous attempt.";
    return (<OkDialogLayout title="Retry provisioning" onOkClick={() => retryProvisioning(environment)} okButtonLabel="Retry" cancelButtonLabel="Cancel">
            <p>{message}</p>
        </OkDialogLayout>);
}
type RetryDeprovisioningDialogProps = {
    environment: ProjectEphemeralEnvironmentDetail;
    project: ProjectResource;
    retryDeprovisioning: (environment: ProjectEphemeralEnvironmentDetail) => Promise<void>;
};
function RetryDeprovisioningDialog({ environment, project, retryDeprovisioning }: RetryDeprovisioningDialogProps) {
    const message = HasRunbooksInGit(project.PersistenceSettings)
        ? "This will retry deprovisioning from the latest commit on the Git ref used in the previous attempt"
        : "This will retry deprovisioning from the latest published snapshot of the runbook used in the previous attempt.";
    return (<OkDialogLayout title="Retry deprovisioning" onOkClick={() => retryDeprovisioning(environment)} okButtonLabel="Retry" cancelButtonLabel="Cancel">
            <p>{message}</p>
        </OkDialogLayout>);
}
type MarkProvisioningSuccessfulDialogProps = {
    environment: ProjectEphemeralEnvironmentDetail;
    markProvisioningSuccessful: (environment: ProjectEphemeralEnvironmentDetail) => Promise<void>;
};
function MarkProvisioningSuccessfulDialog({ environment, markProvisioningSuccessful }: MarkProvisioningSuccessfulDialogProps) {
    return (<OkDialogLayout title="Mark provisioning as successful" onOkClick={() => markProvisioningSuccessful(environment)} okButtonLabel="Mark as successful" cancelButtonLabel="Cancel">
            <p>This will mark the provisioning of the environment as successful, ignoring the provisioning failure.</p>
            <Callout title="Warning" type="warning">
                Use this option only if you are confident the environment has been successfully provisioned, despite the provisioning runbook failure.
            </Callout>
        </OkDialogLayout>);
}
type MarkDeprovisioningSuccessfulDialogProps = {
    environment: ProjectEphemeralEnvironmentDetail;
    markDeprovisioningSuccessful: (environment: ProjectEphemeralEnvironmentDetail) => Promise<void>;
};
function MarkDeprovisioningSuccessfulDialog({ environment, markDeprovisioningSuccessful }: MarkDeprovisioningSuccessfulDialogProps) {
    return (<OkDialogLayout title="Mark deprovisioning as successful" onOkClick={() => markDeprovisioningSuccessful(environment)} okButtonLabel="Mark as successful" cancelButtonLabel="Cancel">
            <p>This will mark the deprovisioning of the environment as successful, ignoring the deprovisioning failure.</p>
            <Callout title="Warning" type="warning">
                Use this option only if you are confident any resources associated with the environment have been properly cleaned up, despite the deprovisioning runbook failing.
            </Callout>
        </OkDialogLayout>);
}
type ProjectsProps = {
    projectDetails: ProjectDetails[] | undefined;
};
function Projects({ projectDetails = [] }: ProjectsProps) {
    const styles = {
        container: css({
            display: "flex",
            flexWrap: "wrap",
            flexDirection: "row",
            alignItems: "center",
            gap: space[8],
        }),
        showAll: css({
            color: themeTokens.color.text.link.default,
            cursor: "pointer",
        }),
    };
    const [showAll, setShowAll] = useState<boolean>(false);
    const useShowAll = projectDetails.length > 1;
    let sortedProjectDetails = projectDetails.toSorted((a, b) => a.Name.localeCompare(b.Name));
    if (!showAll) {
        sortedProjectDetails = sortedProjectDetails.slice(0, 1);
    }
    const tags = sortedProjectDetails.map((p) => <Tag label={p.Name} prefix={<Avatar type="project" src={client.resolve(p.Logo)} size="small"/>}/>);
    const moreProjectsToShow = projectDetails.length - 1;
    const showAllButtonLabel = showAll ? "Show summary" : `+${moreProjectsToShow} ${pluralize("project", moreProjectsToShow)}`;
    return (<div className={styles.container}>
            {tags}
            {useShowAll && (<span className={styles.showAll} onClick={() => setShowAll(!showAll)}>
                    {showAllButtonLabel}
                </span>)}
        </div>);
}
